/**
 * Farm & Acre Co - Dynamic Pricing Engine
 *
 * Calculates real-time price based on:
 * - Base product price × quantity
 * - State rebates (sheep NLIS only)
 * - SA levy ($1.50/tag when state = SA and product is NLIS)
 * - Print setup fees (standard/non-standard/complex)
 *
 * Renders a price breakdown display that updates on field changes.
 */

if (!customElements.get('allflex-pricing')) {
  class AllflexPricing extends HTMLElement {
    constructor() {
      super();
      this.basePrice = parseFloat(this.dataset.basePrice) || 0;
      this.formTemplate = this.dataset.formTemplate || '';
      this.isNlisSheep = this.dataset.isNlisSheep === 'true';
      this.showSaLevy = this.dataset.showSaLevy === 'true';
      this.saLevyAmount = parseFloat(this.dataset.saLevyAmount) || 1.50;
      this.vidFee = parseFloat(this.dataset.vidFee) || 0;
      this.tsuFee = parseFloat(this.dataset.tsuFee) || 0;
      this.debounceTimer = null;
    }

    connectedCallback() {
      this.quantityInput = document.querySelector('[data-allflex-field="quantity"], .quantity__input[form]');
      this.stateSelect = document.getElementById('allflex-state');
      this.buttonPairingYes = document.getElementById('matching-buttons-yes');
      this.buttonPairingNo = document.getElementById('matching-buttons-no');
      this.breakdownEl = this.querySelector('.allflex-pricing__breakdown');
      this.variantInput = document.querySelector('input.product-variant-id, input[name="id"]');
      this.variantData = this.loadVariantData();

      this.bindEvents();
      this.calculate();
      // SPACEY: Initialize file upload visibility on page load (hide for Standard Print, show for Non-Standard/Complex)
      this.updateFileUploadVisibility();
    }

    /**
     * Load variant data from embedded JSON (for dynamic base price updates)
     */
    loadVariantData() {
      const el = document.getElementById('AllflexVariantData');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    /**
     * Update base price from current variant selection
     */
    updateBasePrice() {
      if (!this.variantInput || !this.variantData) return;
      const variantId = parseInt(this.variantInput.value);
      const variant = this.variantData.find(v => v.id === variantId);
      if (variant) {
        this.basePrice = variant.price / 100;
      }
    }

    bindEvents() {
      const fields = [this.quantityInput, this.stateSelect, this.buttonPairingYes, this.buttonPairingNo];
      fields.forEach(field => {
        if (field) {
          field.addEventListener('change', () => this.debouncedCalculate());
          field.addEventListener('input', () => this.debouncedCalculate());
        }
      });

      // Listen for print type radio button changes
      const printTypeRadios = document.querySelectorAll('[data-allflex-field="print-type"]');
      printTypeRadios.forEach(radio => {
        radio.addEventListener('change', () => {
          this.debouncedCalculate();
          // SPACEY: Update file upload visibility when print type changes (show for Non-Standard/Complex, hide for Standard)
          this.updateFileUploadVisibility();
        });
      });

      // Listen for the FOFIA Print Method radio (Laser / Pitch Black) — it changes the
      // per-line fee (pitch black costs more), so the estimate must recalc on switch.
      document.querySelectorAll('[data-allflex-field="print-method"]').forEach(radio => {
        radio.addEventListener('change', () => this.debouncedCalculate());
      });

      // Listen for the reverse/back-print checkbox — ticking it adds a per-tag fee,
      // so the on-page estimate must recalc to match the cart charge.
      document.querySelectorAll('[data-allflex-field="fofia-back-print"]').forEach(box => {
        box.addEventListener('change', () => this.debouncedCalculate());
      });

      // Listen for print style dropdown changes (program-based selectors)
      const printStyleSelects = document.querySelectorAll(
        '[data-allflex-field="print-style"], [data-allflex-field="print-style-female"], [data-allflex-field="print-style-male"]'
      );
      printStyleSelects.forEach(select => {
        select.addEventListener('change', () => this.debouncedCalculate());
      });

      // Listen for hidden print-lines fee input changes (updated by print-line-inputs.js)
      const printLinesInputs = document.querySelectorAll(
        '[data-allflex-field="print-lines"], [data-allflex-field="print-lines-female"], [data-allflex-field="print-lines-male"]'
      );
      printLinesInputs.forEach(input => {
        input.addEventListener('change', () => this.debouncedCalculate());
      });

      // SPACEY: Listen for hidden print-sides fee input changes (updated by print-line-inputs.js)
      const printSidesInput = document.querySelector('[data-allflex-field="print-sides-hidden"]');
      if (printSidesInput) {
        printSidesInput.addEventListener('change', () => this.debouncedCalculate());
      }

      // Listen for color selection changes (for state rebate calculation)
      const colorFields = document.querySelectorAll('[data-allflex-field="color"]');
      colorFields.forEach(field => {
        field.addEventListener('change', () => this.debouncedCalculate());
      });

      // Listen for quantity changes from Dawn's quantity-input component
      document.addEventListener('change', (e) => {
        if (e.target.closest('quantity-input') && e.target.classList.contains('quantity__input')) {
          this.quantityInput = e.target;
          this.debouncedCalculate();
        }
      });

      // Listen for variant changes (updates base price when colour/tier changes)
      if (this.variantInput) {
        this.variantInput.addEventListener('change', () => {
          this.updateBasePrice();
          this.debouncedCalculate();
        });
      }
    }

    debouncedCalculate() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.calculate(), 150);
    }

    getQuantity() {
      if (this.quantityInput) {
        return Math.max(1, parseInt(this.quantityInput.value) || 1);
      }
      return 1;
    }

    getState() {
      return this.stateSelect ? this.stateSelect.value : '';
    }

    getPrintSetupFee() {
      const checkedRadio = document.querySelector('[data-allflex-field="print-type"]:checked');
      if (!checkedRadio) return 0;
      const val = checkedRadio.value;
      if (val === 'Non-Standard Print') return 71.50;
      if (val === 'Complex Print') return 137.50;
      return 0;
    }

    /**
     * SPACEY: Update file upload field visibility based on print-type selection
     * Shows upload field only for Non-Standard Print and Complex Print
     * Hides for Standard Print (default) and clears any selected file
     */
    updateFileUploadVisibility() {
      const uploadField = document.getElementById('field-file-upload');
      if (!uploadField) return;

      const checkedRadio = document.querySelector('[data-allflex-field="print-type"]:checked');
      if (!checkedRadio) return;

      const printType = checkedRadio.value;
      const shouldShow = printType === 'Non-Standard Print' || printType === 'Complex Print';

      if (shouldShow) {
        uploadField.style.display = '';
      } else {
        uploadField.style.display = 'none';
        // SPACEY: Clear file input when hiding to prevent unwanted uploads (switching from Complex/Non-Standard back to Standard)
        const fileInput = document.getElementById('allflex-file-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        // SPACEY: Also clear the hidden filename input that stores the uploaded file reference
        const filenameInput = uploadField.querySelector('input[data-upload-role="filename"]');
        if (filenameInput) {
          filenameInput.value = '';
        }
      }
    }

    /**
     * Get per-line print fees for the selected print options
     * Returns { totalPerTag, details: [{side, lines, pricePerTag}] }
     */
    getLinePrintFees() {
      const LINE_FEE_PRICES = {
        'blank': 0,
        '1-line': 0.46,
        '2-lines': 0.75,
        '3-lines': 0.95,
        'custom': 1.21
      };

      const LINE_FEE_LABELS = {
        '1-line': '1 Line',
        '2-lines': '2 Lines',
        '3-lines': '3 Lines',
        'custom': 'Custom'
      };

      // FOFIA line-print fees (order form, inc GST) — laser vs pitch black x line count.
      // These mirror the ACTUAL cart charge (fee-auto-add.js -> fofia-print-fee variants,
      // verified $0.45/$0.55/$0.65 laser, $0.75/$0.85/$0.95 pitch), so the on-page estimate
      // matches checkout. The Print Method radio only renders on FOFIA line-print tags, so
      // Allflex tags never enter this branch and keep the flat LINE_FEE_PRICES above.
      const FOFIA_LINE_FEE_PRICES = {
        'Laser-1-line': 0.45, 'Laser-2-lines': 0.55, 'Laser-3-lines': 0.65,
        'Pitch Black-1-line': 0.75, 'Pitch Black-2-lines': 0.85, 'Pitch Black-3-lines': 0.95
      };
      // Reverse/back-print add-on fee (order-form parity) — mirrors fee-auto-add.js /
      // FOFIA_BACK_PRINT_FEES so the estimate matches the cart charge.
      const FOFIA_BACK_PRINT_PRICES = { 'Laser': 0.30, 'Pitch Black': 0.95 };
      const isFofiaLinePrint = !!document.querySelector('[data-allflex-field="print-method"]');
      const fofiaMethod = () => {
        const el = document.querySelector('[data-allflex-field="print-method"]:checked');
        return el ? el.value : 'Laser';
      };

      const details = [];
      let totalPerTag = 0;

      // Helper: read fee value from either a hidden input or a select dropdown
      function readFeeValue(selector) {
        const el = document.querySelector(selector);
        if (!el) return null;
        const val = el.value;
        if (!val || val === 'blank') return null;
        // If it's a hidden input, the value is the fee tier directly
        // If it's a select, the value might be a program ID - read data-fee-value from selected option
        if (el.tagName === 'SELECT') {
          const opt = el.options[el.selectedIndex];
          return opt ? (opt.dataset.feeValue || val) : val;
        }
        return val;
      }

      function getLabelForFee(feeValue) {
        return LINE_FEE_LABELS[feeValue] || feeValue;
      }

      // Single print lines (visual_tag template). FOFIA line-print tags price by print
      // method (Laser/Pitch Black) x line count; Allflex uses the flat map unchanged.
      const singleFeeValue = readFeeValue('[data-allflex-field="print-lines"]');
      if (singleFeeValue) {
        const method = fofiaMethod();
        const pricePerTag = isFofiaLinePrint
          ? (FOFIA_LINE_FEE_PRICES[method + '-' + singleFeeValue] || 0)
          : (LINE_FEE_PRICES[singleFeeValue] || 0);
        if (pricePerTag > 0) {
          details.push({
            side: null,
            lines: isFofiaLinePrint ? (method + ' ' + getLabelForFee(singleFeeValue)) : getLabelForFee(singleFeeValue),
            pricePerTag
          });
          totalPerTag += pricePerTag;
        }
      }

      // Reverse/back-print add-on (FOFIA line-print tags) — per-tag fee that follows the
      // front print method, added when the "print the back" box is ticked.
      if (isFofiaLinePrint) {
        const backBox = document.querySelector('[data-allflex-field="fofia-back-print"]');
        if (backBox && backBox.checked) {
          const method = fofiaMethod();
          const backPerTag = FOFIA_BACK_PRINT_PRICES[method] || 0;
          if (backPerTag > 0) {
            details.push({ side: null, lines: method + ' back print', pricePerTag: backPerTag });
            totalPerTag += backPerTag;
          }
        }
      }

      // Female print lines (nlis_cattle template)
      const femaleFeeValue = readFeeValue('[data-allflex-field="print-lines-female"]');
      if (femaleFeeValue) {
        const pricePerTag = LINE_FEE_PRICES[femaleFeeValue] || 0;
        if (pricePerTag > 0) {
          details.push({
            side: 'Female',
            lines: getLabelForFee(femaleFeeValue),
            pricePerTag
          });
          totalPerTag += pricePerTag;
        }
      }

      // Male print lines (nlis_cattle template)
      const maleFeeValue = readFeeValue('[data-allflex-field="print-lines-male"]');
      if (maleFeeValue) {
        const pricePerTag = LINE_FEE_PRICES[maleFeeValue] || 0;
        if (pricePerTag > 0) {
          details.push({
            side: 'Male',
            lines: getLabelForFee(maleFeeValue),
            pricePerTag
          });
          totalPerTag += pricePerTag;
        }
      }

      return { totalPerTag, details };
    }

    getStateRebate(state, selectedColor) {
      // Rebates apply only to sheep/goat eID tags
      if (!this.isNlisSheep) return 0;

      // Get the selected color from the color field
      const color = selectedColor || this.getSelectedColor();
      if (!color) return 0;

      // State-specific rebates (all values ex-GST)
      switch (state) {
        // TAS: retired 2026-08-03 — the Tasmanian sheep-tag rebate ENDED. Tasmania now pays
        // NSW-equivalent via the repriced `State Pricing = TAS` variants, so any value here
        // would be a phantom saving the cart will not honour.
        // Do not restore: re-enabling the rebate is a variant PRICE change, not a theme change.

        case 'SA':
          // South Australia: $0.475/tag for Orange (2026) ONLY at POS
          return (color === 'Orange') ? 0.475 : 0;

        case 'WA':
          // Western Australia: $0.60/tag for Orange, Pink, Yellow ONLY
          const waEligibleColors = ['Orange', 'Pink', 'Yellow'];
          return waEligibleColors.includes(color) ? 0.60 : 0;

        default:
          return 0;
      }
    }

    getSelectedColor() {
      // Get color from radio buttons or select dropdown
      // Values are like "Orange (Year 2026)" - parse to just "Orange"
      const colorRadio = document.querySelector('[data-allflex-field="color"]:checked');
      if (colorRadio) return colorRadio.value.split(' (')[0];

      const colorSelect = document.querySelector('[data-allflex-field="color"]');
      if (colorSelect && colorSelect.value) return colorSelect.value.split(' (')[0];

      return null;
    }

    getSaLevy(state, quantity) {
      if (!this.showSaLevy || state !== 'SA') return 0;
      return this.saLevyAmount * quantity;
    }

    getButtonPairingCost() {
      if (!this.buttonPairingYes || !this.buttonPairingYes.checked) return 0;
      const pairingEl = document.getElementById('AllflexButtonPairing');
      if (!pairingEl) return 0;
      const buttonPriceRaw = parseFloat(pairingEl.dataset.buttonPrice) || 0;
      // Shopify Liquid outputs variant.price in cents, convert to dollars
      const buttonPrice = buttonPriceRaw / 100;
      return buttonPrice * this.getQuantity();
    }

    // SPACEY: Calculate side printing fee for sheep/goat/feedlot products (1 side / 2 sides)
    // SPACEY: Read from dropdown (not hidden input) because browser caches dropdown values during back/forward navigation
    getSideFeeCost() {
      const SIDE_FEE_PRICES = {
        'No Print': 0,
        'Printed 1 Side': 0.33,
        'Printed 2 Sides': 0.48
      };

      // SPACEY: Read from dropdown directly (browser caches this, unlike hidden inputs which reset on navigation)
      const printSidesInput = document.querySelector('[data-allflex-field="print-sides"]');
      if (!printSidesInput) return 0;

      const val = printSidesInput.value;
      if (!val || val === 'No Print') return 0;

      const pricePerTag = SIDE_FEE_PRICES[val] || 0;
      return pricePerTag * this.getQuantity();
    }

    calculate() {
      const quantity = this.getQuantity();
      const state = this.getState();
      const printSetupFee = this.getPrintSetupFee();
      const linePrintFees = this.getLinePrintFees();
      const linePrintTotal = linePrintFees.totalPerTag * quantity;
      const saLevy = this.getSaLevy(state, quantity);
      const buttonCost = this.getButtonPairingCost();
      // SPACEY: Calculate side printing fee for sheep/goat/feedlot products
      const sideFee = this.getSideFeeCost();
      // SPACEY: Calculate side fee price per tag for display
      const sideFeePricePerTag = quantity > 0 ? sideFee / quantity : 0;

      // For combo products, decompose base price into NLIS + VID + TSU
      const nlisPrice = this.vidFee > 0 ? (this.basePrice - this.vidFee - this.tsuFee) : this.basePrice;

      // Determine rebate: variant-based (sheep with State Pricing option) or legacy
      const stateTier = this.stateTier || '';
      let rebatePerTag = 0;
      let displayBasePrice = this.basePrice;

      if (stateTier && stateTier !== 'Standard' && this.standardPrice > this.basePrice) {
        // Variant-based: rebate is already baked into variant price
        // Show standard price as reference, rebate = difference
        rebatePerTag = this.standardPrice - this.basePrice;
        displayBasePrice = this.standardPrice;
      } else {
        // Legacy: hardcoded rebate table
        rebatePerTag = this.getStateRebate(state);
      }

      const tagSubtotal = displayBasePrice * quantity;
      const rebateAmount = rebatePerTag * quantity;
      const tagTotal = tagSubtotal - rebateAmount;
      // SPACEY: Include side printing fee in grand total
      const grandTotal = tagTotal + saLevy + printSetupFee + linePrintTotal + buttonCost + sideFee;

      this.renderBreakdown({
        quantity,
        basePrice: this.basePrice,
        displayBasePrice,
        nlisPrice,
        vidFee: this.vidFee,
        tsuFee: this.tsuFee,
        vidColor: this.getSelectedColor(),
        tagSubtotal,
        rebatePerTag,
        rebateAmount,
        tagTotal,
        saLevy,
        printSetupFee,
        linePrintFees,
        linePrintTotal,
        buttonCost,
        // SPACEY: Pass side fee and price per tag to breakdown renderer
        sideFee,
        sideFeePricePerTag,
        grandTotal,
        state,
        stateTier,
      });

      // Update SA levy display in compliance section
      this.updateSaLevyDisplay(quantity, saLevy);

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('pricing:updated', {
        bubbles: true,
        // SPACEY: Include side fee in pricing updated event
        detail: { grandTotal, quantity, saLevy, printSetupFee, linePrintTotal, buttonCost, sideFee }
      }));
    }

    updateSaLevyDisplay(quantity, levy) {
      const qtyEl = document.getElementById('sa-levy-qty');
      const totalEl = document.getElementById('sa-levy-total');
      if (qtyEl) qtyEl.textContent = quantity;
      if (totalEl) totalEl.textContent = this.formatMoney(levy);
    }

    formatMoney(cents_or_dollars) {
      // Shopify prices are in cents, but our base price is already in dollars
      const amount = typeof cents_or_dollars === 'number' ? cents_or_dollars : 0;
      return '$' + amount.toFixed(2);
    }

    renderBreakdown(data) {
      if (!this.breakdownEl) return;

      let html = '<div class="allflex-pricing__lines">';

      if (data.vidFee > 0) {
        // Combo product: show itemized NLIS + VID + TSU components
        html += `
          <div class="allflex-pricing__line">
            <span>NLIS Tag: ${this.formatMoney(data.nlisPrice)} &times; ${data.quantity}</span>
            <span>${this.formatMoney(data.nlisPrice * data.quantity)}</span>
          </div>
        `;

        const colorLabel = data.vidColor ? ` (${data.vidColor})` : '';
        html += `
          <div class="allflex-pricing__line">
            <span>VID Tag incl. Tray${colorLabel}: ${this.formatMoney(data.vidFee)} &times; ${data.quantity}</span>
            <span>${this.formatMoney(data.vidFee * data.quantity)}</span>
          </div>
        `;

        if (data.tsuFee > 0) {
          html += `
            <div class="allflex-pricing__line">
              <span>TSU Stick: ${this.formatMoney(data.tsuFee)} &times; ${data.quantity}</span>
              <span>${this.formatMoney(data.tsuFee * data.quantity)}</span>
            </div>
          `;
        }
      } else {
        // Non-combo product: single base line (shows standard price when rebate active)
        html += `
          <div class="allflex-pricing__line">
            <span>${this.formatMoney(data.displayBasePrice)} &times; ${data.quantity} tags</span>
            <span>${this.formatMoney(data.tagSubtotal)}</span>
          </div>
        `;
      }

      // Rebate (if applicable)
      if (data.rebateAmount > 0) {
        const rebateLabel = data.stateTier
          ? `${data.state} rebate: -${this.formatMoney(data.rebatePerTag)}/tag`
          : `State rebate (${data.state}) - ${this.formatMoney(data.rebatePerTag)}/tag`;
        html += `
          <div class="allflex-pricing__line allflex-pricing__line--discount">
            <span>${rebateLabel}</span>
            <span>-${this.formatMoney(data.rebateAmount)}</span>
          </div>
        `;
      }

      // Per-line print fees
      if (data.linePrintFees && data.linePrintFees.details.length > 0) {
        for (const lineFee of data.linePrintFees.details) {
          const sideLabel = lineFee.side ? ` (${lineFee.side})` : '';
          const feeTotal = lineFee.pricePerTag * data.quantity;
          html += `
            <div class="allflex-pricing__line allflex-pricing__line--fee">
              <span>Print${sideLabel}: ${lineFee.lines} (${data.quantity} × ${this.formatMoney(lineFee.pricePerTag)})</span>
              <span>+${this.formatMoney(feeTotal)}</span>
            </div>
          `;
        }
      }

      // SA Levy
      if (data.saLevy > 0) {
        html += `
          <div class="allflex-pricing__line allflex-pricing__line--levy">
            <span>SA Biosecurity Levy (${data.quantity} &times; $1.50)</span>
            <span>+${this.formatMoney(data.saLevy)}</span>
          </div>
        `;
      }

      // Print setup fee
      if (data.printSetupFee > 0) {
        html += `
          <div class="allflex-pricing__line allflex-pricing__line--fee">
            <span>Print setup fee</span>
            <span>+${this.formatMoney(data.printSetupFee)}</span>
          </div>
        `;
      }

      // Button pairing cost
      if (data.buttonCost > 0) {
        html += `
          <div class="allflex-pricing__line allflex-pricing__line--buttons">
            <span>Matching buttons (${data.quantity})</span>
            <span>+${this.formatMoney(data.buttonCost)}</span>
          </div>
        `;
      }

      // SPACEY: Side printing fee (sheep/goat/feedlot products)
      if (data.sideFee > 0) {
        const printSidesSelect = document.querySelector('[data-allflex-field="print-sides"]');
        const sidesLabel = printSidesSelect ? printSidesSelect.value : 'Side printing';
        html += `
          <div class="allflex-pricing__line allflex-pricing__line--fee">
            <span>${sidesLabel} (${data.quantity} × ${this.formatMoney(data.sideFeePricePerTag)})</span>
            <span>+${this.formatMoney(data.sideFee)}</span>
          </div>
        `;
      }

      // Total
      html += `
        <div class="allflex-pricing__line allflex-pricing__line--total">
          <span>Estimated Total</span>
          <strong>${this.formatMoney(data.grandTotal)}</strong>
        </div>
      `;

      html += '</div>';

      if (data.state === 'SA' && data.saLevy > 0) {
        html += '<p class="allflex-pricing__note">* SA Biosecurity Levy is added at checkout</p>';
      }
      if (data.printSetupFee > 0) {
        html += '<p class="allflex-pricing__note">* Print setup is a one-time fee per order</p>';
      }

      this.breakdownEl.innerHTML = html;
    }
  }

  customElements.define('allflex-pricing', AllflexPricing);
}
